import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Clock, BookOpen, Check } from 'lucide-react';
import { courses } from '../data/courses';
import { useCart } from '../context/CartContext';
import copy from '../copy';

const CourseDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { addItem } = useCart();
  
  const course = courses.find(c => c.id === id);

  if (!course) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Curso no encontrado</h1>
          <Link to="/cursos" className="btn-primary">
            Volver a los cursos
          </Link>
        </div>
      </div>
    );
  }

  const handleAddToCart = () => {
    addItem(course);
  };

  const formatPrice = (price: number) => {
    return `${copy.comun_simbolo}${price} ${copy.comun_moneda}`;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Link to="/" className="hover:text-primary-600">{copy.nav_inicio}</Link>
            <span>/</span>
            <Link to="/cursos" className="hover:text-primary-600">{copy.nav_cursos}</Link>
            <span>/</span>
            <span className="text-gray-900">{course.title}</span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Course Header */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-primary-600 font-medium">{course.category}</span>
                  <span className="text-gray-400">•</span>
                  <span className="text-sm text-gray-600">{course.level}</span>
                </div>
                
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                  {course.title}
                </h1>
                
                <p className="text-lg text-gray-600 leading-relaxed">
                  {course.description}
                </p>

                <div className="flex items-center space-x-6 text-sm text-gray-600">
                  <div className="flex items-center space-x-1">
                    <Clock className="w-4 h-4" />
                    <span>{course.duration}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <BookOpen className="w-4 h-4" />
                    <span>{course.lessons} {copy.detalle_lecciones}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Course Image */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
              <img
                src={course.image}
                alt={course.title}
                className="w-full h-64 md:h-96 object-cover"
              />
            </div>

            {/* What You'll Learn */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {copy.detalle_que_aprenderas}
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {course.whatYouWillLearn.map((item, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Requirements */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {copy.detalle_requisitos}
              </h2>
              <ul className="space-y-3">
                {course.requirements.map((requirement, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <div className="w-2 h-2 bg-primary-600 rounded-full mt-2 flex-shrink-0" />
                    <span className="text-gray-700">{requirement}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Course Features */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                {copy.detalle_caracteristicas}
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {course.features.map((feature, index) => (
                  <div key={index} className="flex items-center space-x-3">
                    <Check className="w-5 h-5 text-green-500" />
                    <span className="text-gray-700">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-8">
              <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                <div className="space-y-6">
                  {/* Price */}
                  <div className="text-center">
                    <p className="text-4xl font-bold text-primary-600">
                      {formatPrice(course.price)}
                    </p>
                  </div>

                  {/* Action Buttons */}
                  <div className="space-y-3">
                    <button
                      onClick={handleAddToCart}
                      className="btn-primary w-full"
                    >
                      {copy.detalle_agregar_carrito}
                    </button>
                  </div>

                  {/* Course Info */}
                  <div className="border-t border-gray-200 pt-6 space-y-4">
                    <div className="flex justify-between">
                      <span className="text-gray-600">{copy.detalle_duracion}:</span>
                      <span className="font-medium">{course.duration}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{copy.cursos_nivel}:</span>
                      <span className="font-medium">{course.level}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{copy.detalle_lecciones}:</span>
                      <span className="font-medium">{course.lessons}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{copy.detalle_certificado}:</span>
                      <span className="font-medium">{course.certificate ? copy.detalle_si : copy.detalle_no}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">{copy.detalle_acceso_vida}:</span>
                      <span className="font-medium">{course.lifetimeAccess ? copy.detalle_si : copy.detalle_no}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetail; 